import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Component, EventEmitter, Directive, Inject, Input, Output, HostBinding, HostListener } from '@angular/core';
import { Destroy } from '@web-bankir/ui-kit/helpers/abstractions';
import { browserMethod } from '@web-bankir/ui-kit/helpers/decorators';
import { takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest } from 'rxjs';
import * as i1 from '@web-bankir/ui-kit/services/device';

var ModalClass;
(function (ModalClass) {
    ModalClass["OPEN"] = "open";
    ModalClass["SHOW"] = "show";
    ModalClass["FADE"] = "fade";
    ModalClass["BACKDROP"] = "backdrop";
})(ModalClass || (ModalClass = {}));

class ModalBackdropComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ModalBackdropComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: ModalBackdropComponent, isStandalone: true, selector: "wb-ui-modal-backdrop", host: { classAttribute: "modal-backdrop" }, ngImport: i0, template: "", styles: [":host{position:fixed;top:0;left:0;z-index:1040;width:100vw;height:100vh}:host.fade{opacity:0}:host.show{opacity:1}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ModalBackdropComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'wb-ui-modal-backdrop', host: {
                        class: 'modal-backdrop',
                    }, template: "", styles: [":host{position:fixed;top:0;left:0;z-index:1040;width:100vw;height:100vh}:host.fade{opacity:0}:host.show{opacity:1}\n"] }]
        }] });

const MODAL_CONFIG_DEFAULTS = {
    show: true,
    backdrop: true,
    ignoreBackdropClick: false,
    skipHeader: false,
};

var ModalPosition;
(function (ModalPosition) {
    ModalPosition["TOP"] = "top";
    ModalPosition["BOTTOM"] = "bottom";
    ModalPosition["RIGHT"] = "right";
    ModalPosition["LEFT"] = "left";
    ModalPosition["CENTER"] = "center";
})(ModalPosition || (ModalPosition = {}));

var ModalClose;
(function (ModalClose) {
    ModalClose["UNSET"] = "unset";
    ModalClose["NAVIGATION"] = "navigation";
    ModalClose["CUSTOM"] = "custom";
    ModalClose["OUTSIDE_CLICK"] = "outside-click";
    ModalClose["X_CLICK"] = "x-click";
})(ModalClose || (ModalClose = {}));

class ModalDirective extends Destroy {
    constructor(document, host, renderer, cdRef, deviceService, viewContainerRef) {
        super();
        this.document = document;
        this.host = host;
        this.renderer = renderer;
        this.cdRef = cdRef;
        this.deviceService = deviceService;
        this.viewContainerRef = viewContainerRef;
        // eslint-disable-next-line @angular-eslint/no-output-on-prefix
        this.onShow = new EventEmitter();
        // eslint-disable-next-line @angular-eslint/no-output-on-prefix
        this.onHide = new EventEmitter();
        this.isShown = false;
        this._config = MODAL_CONFIG_DEFAULTS;
        this._positionConfig$ = new BehaviorSubject(void (0));
    }
    onClick($event) {
        if (this.isShown &&
            this.backdrop &&
            !this._config.ignoreBackdropClick &&
            $event.target === this.host.nativeElement) {
            this.hide(undefined, ModalClose.OUTSIDE_CLICK);
        }
    }
    calculatePosition() {
        combineLatest([
            this.deviceService.currentBreakpoints$,
            this._positionConfig$,
        ]).pipe(takeUntil(this.destroy$), tap(([breakpoints, positions]) => {
            const matchedPositions = breakpoints
                .map((breakpoint) => positions?.[breakpoint])
                .filter(pos => !!pos);
            const position = matchedPositions[matchedPositions.length - 1] || [];
            this.position = {
                position,
                top: position.includes(ModalPosition.TOP),
                bottom: position.includes(ModalPosition.BOTTOM),
                left: position.includes(ModalPosition.LEFT),
                right: position.includes(ModalPosition.RIGHT),
                center: position.includes(ModalPosition.CENTER),
            };
            [...this.host.nativeElement.classList].forEach((className) => {
                if (className.includes('modal-position')) {
                    this.removeClass(this.host.nativeElement, className);
                }
            });
            this.position.position.forEach((className) => this.addClass(this.host.nativeElement, `modal-position-${className}`));
        })).subscribe();
    }
    ngOnChanges(changes) {
        if (changes.config) {
            this.updateConfig();
        }
        if (changes.positionConfig) {
            this._positionConfig$.next(changes.positionConfig.currentValue);
        }
    }
    ngAfterViewInit() {
        this.calculatePosition();
        this.updateConfig();
        if (this._config.show) {
            this.show();
        }
        this.cdRef.detectChanges();
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.hide(false);
    }
    show() {
        if (this.isShown) {
            return;
        }
        this.onShow.emit(this);
        this.isShown = true;
        this.addClass(this.document?.documentElement, ModalClass.OPEN);
        if (this._config.backdrop) {
            this.addClass(this.document?.documentElement, ModalClass.BACKDROP);
        }
        this.showBackdrop();
    }
    hide(emit = true, clickPlace = ModalClose.UNSET) {
        if (!this.isShown) {
            return;
        }
        if (emit) {
            this.onHide.emit(clickPlace);
        }
        this.isShown = false;
        this.removeClass(this.document?.documentElement, ModalClass.OPEN);
        this.removeClass(this.document?.documentElement, ModalClass.BACKDROP);
        this.hideBackdrop();
    }
    updateConfig() {
        this._config = { ...MODAL_CONFIG_DEFAULTS, ...this.config };
        if (this.backdrop && !this._config.backdrop) {
            this.hideBackdrop();
        }
        if (this._config.backdrop) {
            this.addClass(this.host.nativeElement, ModalClass.BACKDROP);
            this.addClass(this.document?.documentElement, ModalClass.BACKDROP);
        }
        else {
            this.removeClass(this.host.nativeElement, ModalClass.BACKDROP);
        }
    }
    showBackdrop() {
        if (this.isShown &&
            this._config.backdrop &&
            !this.backdrop) {
            this.backdrop = this.viewContainerRef.createComponent(ModalBackdropComponent);
        }
    }
    hideBackdrop() {
        if (this.backdrop) {
            this.backdrop.destroy();
        }
    }
    addClass(element, className) {
        if (element) {
            if (!element.classList.contains(className)) {
                this.renderer.addClass(element, className);
            }
        }
    }
    removeClass(element, className) {
        if (element) {
            if (element.classList.contains(className)) {
                this.renderer.removeClass(element, className);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ModalDirective, deps: [{ token: 'Document' }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i1.DeviceService }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: ModalDirective, isStandalone: true, selector: "[chUiModal]", inputs: { config: "config", positionConfig: ["position", "positionConfig"] }, outputs: { onShow: "onShow", onHide: "onHide" }, host: { listeners: { "click": "onClick($event)" }, properties: { "class.show": "this.isShown" } }, exportAs: ["modal"], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
__decorate([
    browserMethod()
], ModalDirective.prototype, "calculatePosition", null);
__decorate([
    browserMethod()
], ModalDirective.prototype, "showBackdrop", null);
__decorate([
    browserMethod()
], ModalDirective.prototype, "hideBackdrop", null);
__decorate([
    browserMethod()
], ModalDirective.prototype, "addClass", null);
__decorate([
    browserMethod()
], ModalDirective.prototype, "removeClass", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ModalDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[chUiModal]',
                    exportAs: 'modal',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['Document']
                }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i1.DeviceService }, { type: i0.ViewContainerRef }], propDecorators: { config: [{
                type: Input
            }], positionConfig: [{
                type: Input,
                args: ['position']
            }], onShow: [{
                type: Output
            }], onHide: [{
                type: Output
            }], isShown: [{
                type: HostBinding,
                args: ['class.show']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], calculatePosition: [], showBackdrop: [], hideBackdrop: [], addClass: [], removeClass: [] } });

class IconModalCloseComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: IconModalCloseComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: IconModalCloseComponent, isStandalone: true, selector: "icon-modal-close", ngImport: i0, template: "<svg width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n  <path d=\"M12.0002 10.586L16.9502 5.63599L18.3642 7.04999L13.4142 12L18.3642 16.95L16.9502 18.364L12.0002 13.414L7.05023 18.364L5.63623 16.95L10.5862 12L5.63623 7.04999L7.05023 5.63599L12.0002 10.586Z\"\n        fill=\"#C5C9D2\"/>\n</svg>\n", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: IconModalCloseComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'icon-modal-close', template: "<svg width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n  <path d=\"M12.0002 10.586L16.9502 5.63599L18.3642 7.04999L13.4142 12L18.3642 16.95L16.9502 18.364L12.0002 13.414L7.05023 18.364L5.63623 16.95L10.5862 12L5.63623 7.04999L7.05023 5.63599L12.0002 10.586Z\"\n        fill=\"#C5C9D2\"/>\n</svg>\n" }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { IconModalCloseComponent, MODAL_CONFIG_DEFAULTS, ModalBackdropComponent, ModalClass, ModalClose, ModalDirective, ModalPosition };
